/**
 * Store map (Leaflet + raster tiles). No framework.
 *
 * Data (stores.json):
 * - name, website, instagram (URL string or null), addressLines (string[]), lat, lng
 * - Legacy: flat "address" string is split on commas into addressLines when addressLines omitted.
 *
 * Distance from ZIP: user enters a US ZIP; we look up its lat/lng via the free Zippopotam API
 * (https://www.zippopotam.us/) then sort stores by haversine distance (miles). No browser geolocation.
 * Requires CSP/connect-src to allow https://api.zippopotam.us on Shopify. file:// preview may block fetch.
 *
 * After a successful ZIP search, the map fits a broad view around the ZIP centroid plus the nearest
 * few stores (padding + maxZoom cap), instead of fitting every marker.
 *
 * Default (no search): if markers span a continent-sized area, we avoid fitBounds(all markers) — that
 * zooms to world level and breaks tiles; we use a fixed lower-48–scale view instead (see constants).
 *
 * Shopify / CSP: https://*.basemaps.cartocdn.com, https://api.zippopotam.us, Leaflet CDN.
 */
(function () {
  'use strict';

  const ATTR_OSM =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  const ATTR_CARTO =
    ATTR_OSM +
    ' &copy; <a href="https://carto.com/attributions" rel="noopener noreferrer">CARTO</a>';

  const CARTO_BASEMAP_KEY = "cb1_2zla_1_ba4361eb829a59f4ea79d3f8";

  const TILE_PROVIDERS = {
    'carto-voyager': {
      url: `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=${CARTO_BASEMAP_KEY}`,
      attribution: ATTR_CARTO,
      maxZoom: 20,
    },
    'carto-positron': {
      url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
      attribution: ATTR_CARTO,
      maxZoom: 20,
    },
  };

  const DEFAULT_TILE_PROVIDER = 'carto-voyager';
  const DEFAULT_PIN_ICON = 'pin-icon.png';

  /**
   * After a ZIP search, the map frames the centroid plus this many closest stores (see
   * fitMapAroundSearchArea). Adjust to include more of the network in the fitted bounds.
   */
  const NEAREST_N_STORES_FOR_MAP_FOCUS = 3;

  /**
   * If all markers span more than this (great-circle diagonal, meters), fitting every marker would
   * zoom to world level and breaks tiles / UX — use a fixed continental view instead.
   */
  const MAX_SPAN_FOR_FIT_ALL_MARKERS_M = 1800000;

  /** When span is huge, show lower-48–friendly framing instead of fitBounds-everything. */
  const DEFAULT_WIDE_US_CENTER = [39.8283, -98.5795];
  const DEFAULT_WIDE_US_ZOOM = 4;

  /** Avoid zoom 0–2 (multi-world tile wrap + grey gutters with some basemaps). */
  const MAP_MIN_ZOOM = 3;

  /** Earth mean radius in miles */
  const R_MI = 3958.7613;

  function scheduleMapSizeRefresh(map) {
    map.invalidateSize();
    requestAnimationFrame(function () {
      map.invalidateSize();
    });
    window.setTimeout(function () {
      map.invalidateSize();
    }, 100);
  }

  /**
   * Initial / reset view (no ZIP search): fit all markers only if they cover a modest area; otherwise
   * use a broad US view so the map stays usable with nationwide data.
   */
  function fitDefaultBoundsForAllStores(map, bounds) {
    if (!bounds || !bounds.isValid()) return;
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const diagonalM = L.latLng(sw).distanceTo(L.latLng(ne));
    if (diagonalM > MAX_SPAN_FOR_FIT_ALL_MARKERS_M) {
      map.setView(DEFAULT_WIDE_US_CENTER, DEFAULT_WIDE_US_ZOOM);
    } else {
      map.fitBounds(bounds, { padding: [36, 36], maxZoom: 14 });
    }
    scheduleMapSizeRefresh(map);
  }

  function tileProviderFor(root) {
    const raw = (root.dataset.tileProvider || '').trim().toLowerCase().replace(/_/g, '-');
    if (raw && TILE_PROVIDERS[raw]) return TILE_PROVIDERS[raw];
    return TILE_PROVIDERS[DEFAULT_TILE_PROVIDER];
  }

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function safeUrl(raw) {
    if (raw == null || typeof raw !== 'string') return '';
    const t = raw.trim();
    if (!t) return '';
    try {
      const u = new URL(t, window.location.href);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
      return u.href;
    } catch {
      return '';
    }
  }

  function normalizeStore(s) {
    const out = {
      name: s.name,
      website: s.website,
      instagram: s.instagram != null ? s.instagram : null,
      lat: s.lat,
      lng: s.lng,
      zip: s.zip,
    };
    let lines = [];
    if (Array.isArray(s.addressLines) && s.addressLines.length) {
      lines = s.addressLines.map(String).map((t) => t.trim()).filter(Boolean);
    } else if (typeof s.address === 'string' && s.address.trim()) {
      lines = s.address
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
    }
    out.addressLines = lines;
    return out;
  }

  function validateStores(data) {
    if (!Array.isArray(data)) return [];
    return data
      .map(normalizeStore)
      .filter(function (s) {
        return (
          s &&
          typeof s.lat === 'number' &&
          typeof s.lng === 'number' &&
          !isNaN(s.lat) &&
          !isNaN(s.lng)
        );
      });
  }

  function storeId(index) {
    return 'store-' + index;
  }

  /** Great-circle distance in miles (WGS84 sphere). */
  function haversineMiles(lat1, lon1, lat2, lon2) {
    const r = Math.PI / 180;
    const a0 = lat1 * r;
    const a1 = lat2 * r;
    const d0 = (lat2 - lat1) * r;
    const d1 = (lon2 - lon1) * r;
    const h =
      Math.sin(d0 / 2) * Math.sin(d0 / 2) +
      Math.cos(a0) * Math.cos(a1) * Math.sin(d1 / 2) * Math.sin(d1 / 2);
    return 2 * R_MI * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  /**
   * Free US ZIP → coordinates (Zippopotam). Returns null if unknown or network error.
   */
  function fetchUsZipLatLng(zip5) {
    const url = 'https://api.zippopotam.us/us/' + encodeURIComponent(zip5);
    return fetch(url, { method: 'GET' })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.places || !data.places.length) return null;
        const p = data.places[0];
        const lat = parseFloat(p.latitude);
        const lng = parseFloat(p.longitude);
        if (isNaN(lat) || isNaN(lng)) return null;
        return { lat: lat, lng: lng };
      })
      .catch(function () {
        return null;
      });
  }

  function parseZipInput(value) {
    const digits = String(value || '').replace(/\D/g, '');
    if (digits.length >= 5) return digits.slice(0, 5);
    return digits;
  }

  /** Sort order for list + map focus; distances only when origin is set. */
  function sortedEntriesFromStores(stores, origin) {
    const entries = stores.map(function (store, index) {
      let dist = null;
      if (origin) {
        dist = haversineMiles(origin.lat, origin.lng, store.lat, store.lng);
      }
      return { store: store, index: index, distanceMiles: dist };
    });
    if (origin) {
      entries.sort(function (a, b) {
        return a.distanceMiles - b.distanceMiles;
      });
    }
    return entries;
  }

  /**
   * Fit map around the searched ZIP and the closest stores — intentionally broad (padding, maxZoom,
   * LatLngBounds.pad) so it feels regional, not street-level.
   */
  function fitMapAroundSearchArea(map, origin, sortedEntries) {
    const k = Math.min(NEAREST_N_STORES_FOR_MAP_FOCUS, sortedEntries.length);
    const latlngs = [L.latLng(origin.lat, origin.lng)];
    for (let i = 0; i < k; i++) {
      latlngs.push(
        L.latLng(sortedEntries[i].store.lat, sortedEntries[i].store.lng)
      );
    }
    let bounds = L.latLngBounds(latlngs);
    if (
      bounds.isValid() &&
      bounds.getNorthEast().equals(bounds.getSouthWest())
    ) {
      bounds = bounds.extend(L.latLng(origin.lat + 0.12, origin.lng + 0.12));
      bounds = bounds.extend(L.latLng(origin.lat - 0.12, origin.lng - 0.12));
    } else if (bounds.isValid()) {
      bounds = bounds.pad(0.22);
    }
    map.fitBounds(bounds, {
      padding: [80, 80],
      maxZoom: 10,
      animate: true,
      duration: 0.55,
    });
    scheduleMapSizeRefresh(map);
  }

  function loadStores(root) {
    const url = (root.dataset.storesUrl || '').trim();
    const isFile = window.location.protocol === 'file:';

    if (!isFile && url) {
      return fetch(url, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (json) {
          return validateStores(json);
        });
    }

    if (Array.isArray(window.__STORE_LOCATIONS__)) {
      return Promise.resolve(validateStores(window.__STORE_LOCATIONS__));
    }

    return Promise.resolve([]);
  }

  function setActive(listRoot, activeRow) {
    listRoot.querySelectorAll('.store-map__row').forEach(function (row) {
      row.classList.toggle('is-active', row === activeRow);
    });
  }

  function escapeHtml(s) {
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  function appendExternalLink(container, href, label) {
    const a = document.createElement('a');
    a.href = href;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.textContent = label;
    container.appendChild(a);
  }

  function buildRowContent(row, store, distanceMiles) {
    const name = store.name || 'Location';
    const web = safeUrl(store.website);
    const ig = safeUrl(store.instagram);

    const title = document.createElement('span');
    title.className = 'store-map__name';
    title.textContent = name;
    row.appendChild(title);

    if (distanceMiles != null && !isNaN(distanceMiles)) {
      const distEl = document.createElement('span');
      distEl.className = 'store-map__distance';
      distEl.textContent = distanceMiles.toFixed(1) + ' mi away';
      row.appendChild(distEl);
    }

    if (web || ig) {
      const linkRow = document.createElement('span');
      linkRow.className = 'store-map__link-row';
      if (web) {
        appendExternalLink(linkRow, web, 'Website');
      }
      if (web && ig) {
        const sep = document.createElement('span');
        sep.className = 'store-map__link-sep';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = '|';
        linkRow.appendChild(sep);
      }
      if (ig) {
        appendExternalLink(linkRow, ig, 'Instagram');
      }
      row.appendChild(linkRow);
    }

    (store.addressLines || []).forEach(function (line) {
      const lineEl = document.createElement('span');
      lineEl.className = 'store-map__address-line';
      lineEl.textContent = line;
      row.appendChild(lineEl);
    });
  }

  function buildPopupHtml(store, distanceMiles) {
    const name = store.name || 'Location';
    const web = safeUrl(store.website);
    const ig = safeUrl(store.instagram);
    let linksHtml = '';
    if (web) {
      linksHtml +=
        '<a href="' +
        escapeHtml(web) +
        '" target="_blank" rel="noopener noreferrer">Website</a>';
    }
    if (web && ig) linksHtml += ' <span aria-hidden="true">|</span> ';
    if (ig) {
      linksHtml +=
        '<a href="' +
        escapeHtml(ig) +
        '" target="_blank" rel="noopener noreferrer">Instagram</a>';
    }
    const addrHtml = (store.addressLines || []).map(escapeHtml).join('<br>');
    let html = '<strong>' + escapeHtml(name) + '</strong>';
    if (distanceMiles != null && !isNaN(distanceMiles)) {
      html +=
        '<br><span class="leaflet-popup-distance">' +
        escapeHtml(distanceMiles.toFixed(1) + ' mi away') +
        '</span>';
    }
    if (linksHtml) html += '<br>' + linksHtml;
    if (addrHtml) html += '<br>' + addrHtml;
    return html;
  }

  function createPinIcon(url) {
    return L.icon({
      iconUrl: url,
      iconSize: [23, 30],
      iconAnchor: [16, 42],
      popupAnchor: [0, -40],
    });
  }

  function init(root) {
    const listEl = qs(root, '.store-map__list');
    const searchInput = qs(root, '.store-map__search-input');
    const searchBtn = qs(root, '.store-map__search-submit');
    const searchMsg = qs(root, '.store-map__search-msg');
    const mapEl = qs(root, '.store-map__map');
    if (!listEl || !mapEl || typeof L === 'undefined') return;

    const pinIconUrl = root.dataset.pinIcon || DEFAULT_PIN_ICON;
    const map = L.map(mapEl, {
      scrollWheelZoom: true,
      minZoom: MAP_MIN_ZOOM,
    });
    const markerIcon = createPinIcon(pinIconUrl);
    const tiles = tileProviderFor(root);
    L.tileLayer(tiles.url, {
      attribution: tiles.attribution,
      maxZoom: tiles.maxZoom,
      referrerPolicy: 'strict-origin-when-cross-origin',
    }).addTo(map);

    const markerLayer = L.layerGroup().addTo(map);

    let origin = null;

    function setSearchMessage(text, isError) {
      if (!searchMsg) return;
      searchMsg.textContent = text || '';
      searchMsg.classList.toggle('store-map__search-msg--error', Boolean(isError && text));
    }

    function setSearchLoading(loading) {
      if (searchBtn) {
        searchBtn.disabled = loading;
        searchBtn.textContent = loading ? '…' : 'Search';
      }
      if (searchInput) searchInput.disabled = loading;
    }

    loadStores(root).then(function (stores) {
      if (stores.length === 0) {
        listEl.innerHTML = '';
        const li = document.createElement('li');
        li.className = 'store-map__item';
        li.textContent =
          'No store locations. For file:// preview, run ./sync-stores.sh after editing stores.json.';
        listEl.appendChild(li);
        map.setView([20, 0], MAP_MIN_ZOOM);
        map.whenReady(function () {
          scheduleMapSizeRefresh(map);
        });
        window.addEventListener('resize', function () {
          map.invalidateSize();
        });
        return;
      }

      function renderList() {
        listEl.innerHTML = '';
        markerLayer.clearLayers();

        const entries = sortedEntriesFromStores(stores, origin);
        const bounds = L.latLngBounds([]);

        listEl.scrollTop = 0;
        entries.forEach(function ({ store, index, distanceMiles }) {
          const lat = store.lat;
          const lng = store.lng;
          const id = storeId(index);

          const li = document.createElement('li');
          li.className = 'store-map__item';

          const row = document.createElement('div');
          row.className = 'store-map__row';
          row.setAttribute('role', 'button');
          row.setAttribute('tabindex', '0');
          row.setAttribute('data-store-id', id);
          row.setAttribute('aria-label', 'Show ' + (store.name || 'location') + ' on map');

          buildRowContent(row, store, distanceMiles);
          li.appendChild(row);
          listEl.appendChild(li);

          const marker = L.marker([lat, lng], { icon: markerIcon });
          marker.bindPopup(buildPopupHtml(store, distanceMiles));
          marker.addTo(markerLayer);

          function focusStoreOnMap() {
            setActive(listEl, row);
            map.flyTo([lat, lng], Math.max(map.getZoom(), 14), { duration: 0.45 });
            marker.openPopup();
          }

          row.addEventListener('click', function (e) {
            if (e.target.closest('a')) return;
            focusStoreOnMap();
          });

          row.addEventListener('keydown', function (e) {
            if (e.target.closest('a')) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              focusStoreOnMap();
            }
          });

          marker.on('click', function () {
            setActive(listEl, row);
          });

          bounds.extend([lat, lng]);
        });

        if (origin) {
          fitMapAroundSearchArea(map, origin, entries);
        } else if (bounds.isValid()) {
          fitDefaultBoundsForAllStores(map, bounds);
        }
        const firstRow = listEl.querySelector('.store-map__row');
        if (firstRow) setActive(listEl, firstRow);
      }

      function runDistanceSearch() {
        setSearchMessage('');
        const zip5 = parseZipInput(searchInput && searchInput.value);
        if (zip5.length !== 5) {
          setSearchMessage('Enter a 5-digit US ZIP code.', true);
          return;
        }

        setSearchLoading(true);
        fetchUsZipLatLng(zip5).then(function (coords) {
          setSearchLoading(false);
          if (!coords) {
            setSearchMessage('That ZIP was not found. Try another US ZIP.', true);
            return;
          }
          origin = coords;
          setSearchMessage('Sorted by distance from ' + zip5 + '.');
          renderList();
        });
      }

      function onSearchInput() {
        if (!searchInput) return;
        if (searchInput.value.trim() !== '') return;
        origin = null;
        setSearchMessage('');
        renderList();
      }

      if (searchBtn) {
        searchBtn.addEventListener('click', runDistanceSearch);
      }
      if (searchInput) {
        searchInput.addEventListener('input', onSearchInput);
        searchInput.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            runDistanceSearch();
          }
        });
      }

      renderList();

      map.whenReady(function () {
        scheduleMapSizeRefresh(map);
      });

      window.addEventListener('resize', function () {
        map.invalidateSize();
      });

      const mapWrap = mapEl.closest('.store-map__map-wrap');
      if (mapWrap && typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(function () {
          map.invalidateSize();
        });
        ro.observe(mapWrap);
      }
    });
  }

  function boot() {
    document.querySelectorAll('[data-store-map]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
